import { useTranslation } from 'react-i18next'
import { useState, type ReactNode } from 'react'
import { Link, type LinkProps } from '@tanstack/react-router'
import type { CommandStatus } from '@gk/contracts'
import { Picto } from './pictos.tsx'

/** A banner that says something about the screen it sits on. Every tone has its night colours (M23 item 3: a cream block on the night background glared). */
export function Banner({ tone = 'info', children, testId }: { tone?: 'info' | 'warn' | 'error' | 'ok'; children: ReactNode; testId?: string }) {
  const cls = {
    info: 'bg-sky-50 text-sky-900 border-sky-300 dark:bg-sky-950/40 dark:text-sky-100 dark:border-sky-600',
    warn: 'bg-amber-50 text-amber-900 border-amber-400 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-600',
    error: 'bg-red-50 text-red-900 border-red-400 dark:bg-red-950/40 dark:text-red-100 dark:border-red-600',
    ok: 'bg-emerald-50 text-emerald-900 border-emerald-400 dark:bg-emerald-950/40 dark:text-emerald-100 dark:border-emerald-600',
  }[tone]
  return <div role={tone === 'error' ? 'alert' : 'status'} className={`rounded-lg border-2 p-3 text-sm ${cls}`} data-tone={tone} data-testid={testId}>{children}</div>
}

/** A short state word in a capsule: 14 px (M23 item 4: 12.75 px was the smallest text on the screen), with its night colours. */
export function Pill({ tone, children }: { tone: 'gray' | 'amber' | 'green' | 'red'; children: ReactNode }) {
  const cls = {
    gray: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100',
    amber: 'bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100',
    green: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100',
    red: 'bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-100',
  }[tone]
  return <span className={`inline-block rounded-full px-3 py-0.5 text-sm font-semibold ${cls}`} data-tone={tone}>{children}</span>
}

export function StatusPill({ status }: { status: CommandStatus }) {
  const { t } = useTranslation()
  const tone = status === 'applied' ? 'green' : status === 'applied_with_exception' ? 'amber' : ['rejected', 'conflict', 'error'].includes(status) ? 'red' : status === 'discarded' ? 'gray' : 'amber'
  return <Pill tone={tone}>{t(`sync.status.${status}`)}</Pill>
}

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string | null; children: ReactNode; htmlFor: string }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-semibold">{label}</label>
      {children}
      {hint && !error && <p className="meta">{hint}</p>}
      {error && <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>}
    </div>
  )
}

/** A password field with an eye button that shows what is typed, and hides it again (the owner, 2026-10-26). Hidden by default and on every new
 *  page (the state is the field's own). Shown as text, a phone keyboard must neither capitalise nor correct it, or the password typed is not the
 *  one saved. The button keeps the caret in the field (no focus change on a mouse press) and is a 56-px square inside the field's right end; Edge's own
 *  reveal button is hidden (two eyes that could disagree). */
export function PasswordInput({ id, value, onChange, autoComplete, required, testId }: {
  id: string; value: string; onChange: (v: string) => void; autoComplete: 'current-password' | 'new-password'; required?: boolean; testId?: string
}) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  return (
    <div className="relative">
      <input id={id} className="input pr-16 [&::-ms-reveal]:hidden" type={shown ? 'text' : 'password'} autoComplete={autoComplete} autoCapitalize="none" autoCorrect="off" spellCheck={false}
        required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      <button type="button" className="absolute inset-y-0 right-0 inline-flex w-14 items-center justify-center rounded-r-xl text-slate-600 hover:text-brand focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand dark:text-slate-300 dark:hover:text-fairway"
        aria-label={shown ? t('auth.hidePassword') : t('auth.showPassword')} aria-controls={id} onMouseDown={(e) => e.preventDefault()} onClick={() => setShown((s) => !s)}
        data-testid={`${testId ?? id}-eye`}>
        <Picto name={shown ? 'eye_off' : 'eye'} />
      </button>
    </div>
  )
}

export function errorText(t: (k: string) => string, code?: string | null): string {
  if (!code) return t('errors.unknown')
  const key = `errors.${code}`
  const s = t(key)
  return s === key ? `${t('errors.unknown')} (${code})` : s
}

export function Section({ title, help, children, testId }: { title: string; help?: string; children: ReactNode; testId?: string }) {
  return (
    <section className="flex flex-col gap-3" data-testid={testId}>
      <h2 className="text-xl font-bold">{title}</h2>
      {help && <p className="meta">{help}</p>}
      {children}
    </section>
  )
}

/** The way back at the top of a page (M23 item 1): a 56-px row where a 21-px underlined text was, with the same words ("← Retour", "← Retour au
 *  planning": the arrow is part of the accessible name the end-to-end tests use, so it stays a text glyph and no pictogram doubles it). A page inside
 *  the router passes `to` (with `params` / `search` when the parent needs them); a page shown outside it (the privacy notice before sign-in) passes `href`. */
export function BackLink({ to, params, search, href, children, testId, className = '' }: {
  to?: LinkProps['to']; params?: LinkProps['params']; search?: LinkProps['search']; href?: string; children: ReactNode; testId?: string; className?: string
}) {
  const cls = `inline-flex min-h-14 w-full items-center gap-2 -mx-2 px-2 text-base font-semibold text-slate-900 active:text-brand-dark dark:text-slate-50 print:hidden ${className}`
  if (href !== undefined) return <a href={href} className={cls} data-testid={testId ?? 'back-link'}>{children}</a>
  return <Link to={to} params={params as never} search={search as never} className={cls} data-testid={testId ?? 'back-link'}>{children}</Link>
}

/** The one line that says why the primary button is disabled (M23 item 6: "Saisissez la pluie pour enregistrer"), right next to it. Rendered only
 *  while `show` is true, so the row disappears the moment the button is live. */
export function DisabledHint({ show = true, children, testId }: { show?: boolean; children: ReactNode; testId?: string }) {
  if (!show) return null
  return <p className="meta" data-testid={testId ?? 'disabled-hint'}>{children}</p>
}

/** The card a page shows instead of its form when the profile cannot use it (M23 item 24): the mechanic who opens /pay by URL sees this, not a
 *  six-digit field and a red 400. Cosmetic like every UI check (the server refuses anyway); `permission` names the code the page needs, which
 *  permissionKeys.test.ts holds to the catalogue. */
export function NotAllowed({ permission }: { permission: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3" data-testid="not-allowed" data-permission={permission}>
      <Banner tone="warn"><p className="font-semibold">{t('common.notAllowedPage')}</p><p className="mt-1">{t('common.notAllowedHint')}</p></Banner>
      <Link to="/" className="btn" data-testid="not-allowed-home"><Picto name="today" className="size-5" />{t('common.today')}</Link>
    </div>
  )
}

/** What an online-first screen says when its command was kept on the device because the network was away (M23 item 10): amber, not red, the form
 *  closed by the caller; the line appears in the list after the next sync. */
export function PendingBanner({ testId }: { testId?: string }) {
  const { t } = useTranslation()
  return <Banner tone="warn" testId={testId ?? 'pending-banner'}><Picto name="sync" className="mr-2 inline size-5 align-text-bottom" />{t('common.pendingOffline')}</Banner>
}
